import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useFeatureModule } from '@/hooks/useFeatureModule';
import { cardStyles } from '@/styles/cards';
import { buttonStyles } from '@/styles/buttons';

interface FeatureModuleGuardProps {
  module: 'attendance' | 'parentPortal' | 'sms' | 'messaging' | 'schoolwideSms';
  children: ReactNode;
  fallback?: ReactNode;
  showLoading?: boolean;
  /**
   * 'enabled' (default): both layers must be on (super-admin grant + school toggle).
   * 'allowed': Layer 1 only — for entitlement-tied pages (e.g. SMS usage/billing)
   * that stay visible when the school pauses the feature.
   */
  layer?: 'enabled' | 'allowed';
}

/**
 * Fallback component for route-level module guards.
 * Shows a "Feature Not Available" card with a link to go home.
 * Pass as `fallback` prop to FeatureModuleGuard when used as a route guard.
 */
export const ModuleNotAvailable = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 1rem' }}>
    <div style={{ ...cardStyles.default, textAlign: 'center', maxWidth: '400px' }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.5rem 0' }}>
        Feature Not Available
      </h2>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 1.5rem 0' }}>
        This feature is not enabled for your school. Contact your administrator for access.
      </p>
      <Link to="/" style={{ ...buttonStyles.primary, textDecoration: 'none' }}>Go Home</Link>
    </div>
  </div>
);

/**
 * Conditionally render children based on module enabled status
 *
 * @example
 * <FeatureModuleGuard module="attendance">
 *   <AttendanceTracking />
 * </FeatureModuleGuard>
 *
 * @example
 * <FeatureModuleGuard
 *   module="attendance"
 *   fallback={<p>Attendance not available</p>}
 * >
 *   <AttendanceButton />
 * </FeatureModuleGuard>
 */
export function FeatureModuleGuard({ module, children, fallback = null, showLoading = true, layer = 'enabled' }: FeatureModuleGuardProps) {
  // Type assertion needed due to function overload limitations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { isEnabled, isAllowed, isLoading } = useFeatureModule(module as any);
  const passes = layer === 'allowed' ? isAllowed : isEnabled;

  // Show loading state (optional)
  if (isLoading && showLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '1rem',
        }}
      >
        <div className="loading-spinner" />
      </div>
    );
  }

  // Module not enabled (or not allowed, when layer='allowed')
  if (!passes) {
    return <>{fallback}</>;
  }

  // Module enabled - render children
  return <>{children}</>;
}
